/* eslint-disable max-len */
import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import { Clock } from '@one-thd/sui-icons';
import { ExperienceContext } from '@thd-nucleus/experience-context';
import classNames from 'classnames';
import helpers, { TIME_UNIT_KEYS } from '../../helpers/countdownHelper';

const labels = {
  months: 'mos',
  days: 'days',
  hours: 'hrs',
  minutes: 'min',
  seconds: 'sec'
};

const TimerEvent = ({
  timeStringAndMessaging,
  shouldUseMessagingString,
  shouldIncludeSvg,
  invertText,
  fontSize,
  textColor,
  inspectorProps
}) => {
  const { channel } = useContext(ExperienceContext);
  const isMobile = channel === 'mobile';
  const style = {
    message: classNames(
      'sui-uppercase',
      { 'sui-text-primary': !invertText && !textColor },
      { 'sui-text-inverse': invertText && !textColor },
      { 'sui-h2-display': fontSize === 'base' && !isMobile },
      { 'sui-h3-display': fontSize === 'small' && !isMobile },
      { 'sui-h4-display': fontSize === 'extraSmall' && !isMobile },
      { 'sui-h4-display': fontSize === 'small' && isMobile },
      { 'sui-h5-display': fontSize === 'extraSmall' && isMobile },
      { 'sui-tracking-normal': fontSize === 'small' && isMobile },
      { 'sui-tracking-normal': fontSize === 'extraSmall' && isMobile },
      { '@lg:sui-text-5xl @md:sui-text-4xl @sm:sui-text-2xl sui-text-base': !isMobile }
    ),
    timer: classNames(
      'sui-flex sui-mr-2 sui-ml-2 sui-items-center',
      { 'sui-text-primary': !invertText && !textColor },
      { 'sui-text-inverse': invertText && !textColor },
      { '@lg:sui-text-5xl @md:sui-text-4xl @sm:sui-text-2xl sui-text-base': !isMobile }
    ),
    countdown: classNames(
      'sui-uppercase',
      { 'sui-text-primary': !invertText && !textColor },
      { 'sui-text-inverse': invertText && !textColor },
      { 'sui-h4-display': !isMobile },
      { 'sui-h2-display': fontSize === 'base' },
      { 'sui-h5-display': fontSize === 'small' && isMobile },
      { 'sui-h6-display': fontSize === 'extraSmall' && isMobile },
      { '@lg:sui-text-4xl @md:sui-text-5xl @sm:sui-text-xl sui-text-base': !isMobile },
      'sui-break-all'
    ),
    timerContainer: classNames(
      'sui-flex sui-pt-2 sui-px-2 sui-justify-center',
      { 'sui-pt-3': fontSize === 'base' || !isMobile },
    ),
    messageContainer: classNames(
      'sui-flex sui-justify-center sui-items-center',
      { 'sui-h3-display': fontSize === 'small' && !isMobile },
      { 'sui-h4-display': fontSize === 'extraSmall' && !isMobile },
      { 'sui-h4-display': fontSize === 'small' && isMobile },
      { 'sui-h5-display': fontSize === 'extraSmall' && isMobile },
      { 'sui-whitespace-nowrap': !isMobile }
    )
  };

  const { messagingString, timeString } = timeStringAndMessaging;
  const units = timeString.units || timeString;
  const visibleKeys = TIME_UNIT_KEYS.filter((key) => units[key] !== undefined);
  const clockFontSize = fontSize === 'base' ? 'large' : 'inherit';
  const elementStyles = {};
  if (textColor) {
    elementStyles.color = textColor;
  }

  return (
    <div data-component="TimerEvent" className="sui-@container" style={elementStyles}>
      <div className={style.messageContainer}>
        <div hidden={!shouldIncludeSvg}>
          <span className={style.timer}>
            <Clock size={clockFontSize} />
          </span>
        </div>
        <span {...helpers.getDataAttributes(inspectorProps?.message)} className={style.message}>
          {(messagingString && shouldUseMessagingString) && (
            messagingString
          )}
        </span>
      </div>
      <div {...helpers.getDataAttributes(inspectorProps?.timer)} className={style.timerContainer}>
        {visibleKeys.map((key, index) => {
          return (
            <span className={style.countdown} key={key} data-testid={`time-unit-${key}`}>
              <span className="sui-pr-1">{units[key]}</span>
              <span className={index < visibleKeys.length - 1 ? 'sui-pr-3' : ''}>{labels[key]}</span>
            </span>
          );
        })}
      </div>
    </div>
  );
};

TimerEvent.propTypes = {
  /**
   * A boolean indicating whether or not you want to lead the timer with "Ends in" or "Ends on"
   */
  shouldUseMessagingString: PropTypes.bool,
  /**
   * To show clock image
   */
  shouldIncludeSvg: PropTypes.bool,
  /**
   * To show orage colour text
   */
  invertText: PropTypes.bool,
  /**
   * To show message and time
   */
  /**
   * To show 1.4em text
   */
  fontSize: PropTypes.string,
  /**
   * To show text colour
   */
  textColor: PropTypes.string,
  inspectorProps: PropTypes.shape({
    message: PropTypes.shape({}),
    timer: PropTypes.shape({})
  }),
  timeStringAndMessaging: PropTypes.shape({
    messagingString: PropTypes.string,
    timeString: PropTypes.shape({
      units: PropTypes.shape({
        months: PropTypes.number,
        days: PropTypes.number,
        hours: PropTypes.number,
        minutes: PropTypes.number,
        seconds: PropTypes.number
      }),
      days: PropTypes.number,
      hours: PropTypes.number,
      minutes: PropTypes.number,
      seconds: PropTypes.number
    })
  })
};

TimerEvent.defaultProps = {
  shouldUseMessagingString: false,
  shouldIncludeSvg: false,
  invertText: false,
  fontSize: 'base',
  textColor: '',
  inspectorProps: null,
  timeStringAndMessaging: {
    messagingString: '',
    timeString: {}
  }
};

export default TimerEvent;
